import { initApiClient } from "../../api/api-client";
import { groq } from "next-sanity";
import { BlogPageTranslations } from "../../model/blog";
import { PortableText } from "@portabletext/react";
import { Navbar } from "../../components/common/navbar/navbar";
import {
  GetStaticPropsContext,
  GetStaticPropsResult,
  GetStaticPaths,
} from "next/types";
import { MainPageData } from "../../model/main-page-data";
import { formatDisplay } from "../../utils/date.utils";
import { useRouter } from "next/router";
import { sanitizeImageUrl } from "../../utils/sanity.utils";
import { LegalDocuments } from "../../model/legal-documents";
import { ChooseFormSection } from "../../sections/choose-form/choose-form";
import { FooterSection } from "../../sections/footer/footer";
import { Bootcamp } from "../../model/bootcamp";
import { CommonTranslates } from "../../model/common-tranlates";
import { blogComponents } from "../../components/blog/common-components";
import { BootcampProvider } from "../../contexts/bootcamp.context";
import Head from "next/head";
import { FormCmsData } from "../../model/form";
import { BannerWithDiscount } from "../../components/common/banner/banner";
import { Recommendations } from "../../sections/recommendations/recommendations";
import { useMemo } from "react";
import { TelegramChat } from "../../components/common/telegram-chat/telegram-chat";
import { Banner } from "../../model/banner";
import { ShareButtonsLink } from "../../components/blog/share-buttons";

interface Props {
  blog: BlogPageTranslations[];
  mainPageData: MainPageData;
  legalDocuments: LegalDocuments;
  bootcamps: Bootcamp[];
  commonTranslates: CommonTranslates;
  forms: FormCmsData;
  banner: Banner;
}

function BlogPage({
  blog,
  mainPageData,
  legalDocuments,
  forms,
  commonTranslates,
  bootcamps,
  banner,
}: Props) {
  const router = useRouter();
  const {
    locale,
    query: { slug },
  } = router;

  const formStyles = useMemo(
    () =>
      ({
        container: "bg-white",
        border: "bg-white border border-solid border-day-base-base-200",
        text: "text-day-base-base-900",
        theme: "light",
      } as const),
    []
  );

  const englishVersion = blog.find((item) => item.slug.current === slug);
  if (!englishVersion) {
    return null;
  }

  const englishVersionId = englishVersion._id;
  const pattern = /__i18n_en$/;

  const cleanId = englishVersionId.replace(pattern, "");

  const russianVersion = blog.find((item) => item._id === cleanId);
  if (!russianVersion) {
    return null;
  }

  const content = locale === "ru" ? russianVersion : englishVersion;
  const textColor = {
    color: content.textColor.hex,
  };
  const borderColor = {
    borderColor: content.textColor.hex,
  };
  const filteredBlogForRecommendation = blog.filter(
    (item) => item._id !== content._id
  );

  return (
    <>
      <Head>
        <title>Guide DAO | Blog</title>
        <meta name="title" content="Guide DAO" />
        <meta name="description" content={content.title} />
        <meta property="og:url" content="https://www.guidedao.xyz/" />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Guide DAO" />
        <meta property="og:description" content={content.title} />
        <meta
          property="og:image"
          content={sanitizeImageUrl(content.image.asset._ref)}
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta property="twitter:domain" content="guidedao.xyz" />
        <meta property="twitter:url" content="https://www.guidedao.xyz/" />
        <meta name="twitter:title" content="Guide DAO" />
        <meta name="twitter:description" content={content.title} />
        <meta
          name="twitter:image"
          content={sanitizeImageUrl(content.image.asset._ref)}
        />
      </Head>
      <BannerWithDiscount
        forms={forms}
        banner={banner}
        orTitle={commonTranslates.orTitle}
        formId={"Попап с баннера в блоге"}
      />
      <BootcampProvider value={{ bootcamps, commonTranslates }}>
        <div className="bg-white">
          <div
            style={{
              backgroundImage: `linear-gradient(to bottom, ${content.color.hex} 0%, white 30%)`,
            }}
          >
            <Navbar
              hasRedirectToHomePage
              color={content.textColor.hex}
              menu={mainPageData.menu}
              loginBtnTitle={mainPageData.login}
            />
            <TelegramChat />
            <div className="max-w-[1440px] m-auto lg:relative pt-35 lg:pt-[95px]">
              <div
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: content.title,
                }}
                style={textColor}
                className="text-center max-w-[700px] md:m-auto text-[28px] leading-[110%] lg:text-[58px] lg:leading-[100%] ${textColor} max-w-[670px] ml-8 mr-8"
              />
              <div
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: content.subtitle,
                }}
                style={textColor}
                className="lg:hidden text-center max-w-[500px] md:m-auto text-[15px] leading-[20px] lg:text-[20px] lg:leading-[124%] lg:pb-[95px] pt-12 lg:pt-15  ml-8 mr-8"
              ></div>
              <div
                style={textColor}
                className="flex flex-col lg:flex-row items-center justify-between pt-12 lg:pt-15 lg:pb-15 m-auto max-w-[1440px] lg:pl-20 lg:pr-20 xxxxl:pl-0 xxxxl:pr-0"
              >
                {content.author && (
                  <div
                    style={borderColor}
                    className="text-[15px] leading-[132%] lg:text-[18px] lg:leading-[132%] border border-solid rounded-[55px] pl-8 pr-8 pt-2 pb-2"
                  >
                    {content.author}
                  </div>
                )}

                <div
                  style={textColor}
                  className="pt-6 lg:pt-0 text-[15px] leading-[132%] lg:text-[18px] lg:leading-[132%]"
                >
                  {formatDisplay(new Date(content.date), locale)}
                </div>
              </div>
              <div
                // eslint-disable-next-line
                dangerouslySetInnerHTML={{
                  __html: content.subtitle,
                }}
                style={textColor}
                className="hidden lg:block text-center max-w-[440px] lg:m-auto text-[15px] leading-[20px] lg:text-[20px] lg:leading-[124%] lg:pb-[95px] pt-12 lg:pt-15 ml-8 mr-8"
              ></div>

              <div>
                <div className="flex items-center justify-center relative top-[10px] lg:top-[0px] pt-10 lg:pt-0 lg:ml-40 lg:mr-40">
                  <img src={sanitizeImageUrl(content.innerImage.asset._ref)} />
                </div>
              </div>
            </div>
            <div className="relative z-10 xl:ml-20 xl:mr-20 bigSize:m-auto max-w-[1840px]">
              <div></div>
              <div className="bg-white pl-11 pr-11 lg:pr-30 lg:pl-30 pt-5 lg:pt-10 rounded-t-[30px] xxxxl:pl-[100px] xxxxl:pr-[100px] bigSize:pl-[240px] bigSize:pr-[240px]">
                <div className="max-w-[1440px]">
                  <PortableText
                    value={content.content}
                    components={blogComponents}
                  />
                </div>
                <ShareButtonsLink
                  copyText={commonTranslates.copyText}
                  copiedText={commonTranslates.copiedText}
                  shareText={commonTranslates.shareText}
                />
              </div>
            </div>
          </div>

          <Recommendations
            title={commonTranslates.recommendationTitle}
            blogContent={filteredBlogForRecommendation}
          />
          <ChooseFormSection
            styles={formStyles}
            formId={"Форма консультации из блога"}
            title={forms.blog.title}
            subtitle={forms.blog.subtitle}
            placeholderEmail={forms.placeholderEmail}
            placeholderTelegram={forms.placeholderTelegram}
            placeholderName={forms.placeholderName}
            button={forms.blog.button}
            selfContact={forms.selfContact}
            selfContactLink={forms.selfContactLink}
            successScreenTitle={forms.successScreenTitle}
            successScreenSubtitle={forms.successScreenSubtitle}
            dataSendWaitMessage={forms.dataSendWaitMessage}
            alertErrorMessage={forms.alertErrorMessage}
            fillTheFormMessage={forms.fillTheFormMessage}
            orTitle={commonTranslates.orTitle}
            fillSvg={"#22282B"}
          />
        </div>
        <div className="bg-black pt-30 rounded-[30px] lg:relative top-[-30px] z-[888]">
          <FooterSection
            contacts={mainPageData.contacts}
            upButtonTitle={mainPageData.upButtonTitle}
            menu={mainPageData.menu}
            forms={forms}
            agreementTitle={legalDocuments.agreementTitle}
            privacyTitle={legalDocuments.privacyTitle}
          />
        </div>
      </BootcampProvider>
    </>
  );
}

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const apiClient = initApiClient();
  const slugsQuery = groq`*[_type == 'blog' && __i18n_lang == "en"]`;

  const blog: BlogPageTranslations[] = await apiClient.fetch(slugsQuery);
  const slugs = blog.map((article) => article.slug.current);

  const paths = slugs.flatMap((slug) =>
    (locales ?? []).map((locale) => ({
      params: { slug },
      locale,
    }))
  );

  return { paths, fallback: false };
};

export async function getStaticProps({
  params = {},
  locale,
}: GetStaticPropsContext): Promise<GetStaticPropsResult<Props>> {
  const apiClient = initApiClient();
  const { slug } = params;
  if (!slug) {
    return { notFound: true };
  }

  const placeholderLocale = locale === "ru" ? "ru" : "en";

  const query = groq`{
      "bootcamps": *[_type == "bootcamps" && __i18n_lang == "${locale}"],
      "blog": *[_type == "blog"],
      "mainPageData": *[_type == "mainPage" && __i18n_lang == "${locale}"],
      "legalDocuments": *[_type == "legalDocuments" && __i18n_lang == "${placeholderLocale}"],
      "commonTranslates": *[_type == "commonTranslates" && __i18n_lang == "${locale}"],
      "forms": *[_type == "forms" && __i18n_lang == "${locale}"],
      "banner": *[_type == "banner" && __i18n_lang == "${locale}"],
    }`;

  const {
    blog,
    legalDocuments,
    bootcamps,
    banner: [banner],
    forms: [forms],
    commonTranslates: [commonTranslates],
    mainPageData: [mainPageData],
  } = await apiClient.fetch(groq`${query}`);

  if (!blog) {
    return { notFound: true };
  }

  return {
    props: {
      blog,
      bootcamps,
      mainPageData,
      legalDocuments,
      forms,
      commonTranslates,
      banner,
    },
  };
}

export default BlogPage;
